import { createContext, useState, useEffect } from "react";
import AsyncStorage from '@react-native-async-storage/async-storage';
import { postApiCall, getApiCall } from '../../service/httpCall';
import { API_BREAK_LIST,
  USER_BREAKS_EDIT, USER_BREAKS_CREATE} from '../../service/url';
import { toast } from 'react-toastify';

// Create Context
export const BreakContext = createContext();

export const BreakProvider = ({ children }) => {
  const [userStatus, setUserStatus] = useState("AVAILABLE");
  const [dotColor, setDotColor] = useState("#28a745");

  const [breakExceeded, setBreakExceeded] = useState(false);
  const [breakEndTime, setBreakEndTime] = useState(null);
  const [countdown, setCountdown] = useState(null);

  const [selectedBreakId, setSelectedBreakId] = useState(null);
  const [breaksData, setBreaksData] = useState([]);

  useEffect(() => {
    if (userStatus !== "BREAK" || !breakEndTime) return;

    const timer = setInterval(() => {
      const remaining = Math.floor((breakEndTime - Date.now()) / 1000);

      if (remaining <= 0) {
        setCountdown(0);
        setBreakExceeded(true);
        clearInterval(timer);
      } else {
        setCountdown(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [userStatus, breakEndTime]);

  useEffect(() => {
    checkAndRestoreBreak();
  }, []);

  const checkAndRestoreBreak = async () => {
    try {
      const breakDataStorage = await AsyncStorage.getItem("activeBreak");
      const user = JSON.parse(await AsyncStorage.getItem("user"));

      if (!breakDataStorage) return;

      const breakArray = JSON.parse(breakDataStorage);

      const userBreak = breakArray.find(
        (b) => b.user_id === user?.id
      );

      if (!userBreak) return;

      const { breakData, endTime } = userBreak;
      const remainingSeconds = Math.floor((endTime - Date.now()) / 1000);

      setUserStatus("BREAK");
      setSelectedBreakId(breakData.id);
      setBreakEndTime(endTime);

      if (remainingSeconds > 0) {
        setCountdown(remainingSeconds);
        setBreakExceeded(false);
      } else {
        setCountdown(0);
        setBreakExceeded(true);
      }
    } catch (e) {
      console.error("Error restoring break state:", e);
      setUserStatus("AVAILABLE");
    }
  };

    const durationToSeconds = (duration) => {
      const minutes = parseInt(duration, 10);
      return minutes * 60;
    };

  const startBreak = async (selectedBreakId) => {
    if (!selectedBreakId) {
      toast.error("Please select a break");
      return;
    }

    try {
      const resp = await postApiCall(USER_BREAKS_CREATE, {
        reason: selectedBreakId,
      });

      const selectedBreak = breaksData.find(
        (b) => b.id == selectedBreakId
      );

      const seconds = durationToSeconds(
        Number(selectedBreak.duration)
      );

      const endTime = Date.now() + seconds * 1000;

      const newBreak = {
        breakData: resp?.userBreaks,
        endTime,
        user_id: resp?.userBreaks?.user_id,
      };

      await AsyncStorage.setItem(
        "activeBreak",
        JSON.stringify([newBreak])
      );

      setBreakEndTime(endTime);
      setCountdown(seconds);
      setUserStatus("BREAK");
      setBreakExceeded(false);

      toast.success("Break Applied");
    } catch (error) {
      console.error(error);
      toast.error("Error while applying break");
    }
  };

  const stopBreak = async (userId) => {
    if (!userId) {
      toast.error("User Id Not Found");
      return;
    }

    try {
      const url = USER_BREAKS_EDIT.replace(":id", userId);
      await postApiCall(url);

      await AsyncStorage.removeItem("activeBreak");

      setUserStatus("AVAILABLE");
      setBreakEndTime(null);
      setCountdown(null);
      setBreakExceeded(false);
      setSelectedBreakId(null);

      toast.success("Break cancelled");
    } catch (error) {
      console.error(error);
      toast.error("Error while cancelling break");
    }
  };

  useEffect(() => {
    getApiCall(`${API_BREAK_LIST}?status=1`, {})
      .then((resp) => setBreaksData(resp))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (userStatus === "AVAILABLE") {
      setDotColor("#28a745");
    } else if (userStatus === "BREAK") {
      setDotColor(breakExceeded ? "#dc3545" : "#ffc107");
    } else {
      setDotColor("#6c757d");
    }
  }, [userStatus, breakExceeded]);

  return (
    <BreakContext.Provider
      value={{
        // state
        userStatus,
        dotColor,
        countdown,
        breakExceeded,
        selectedBreakId,
        breaksData,

        // actions
        startBreak,
        stopBreak,
        setSelectedBreakId,
      }}
    >
      {children}
    </BreakContext.Provider>
  );
};